import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { CURRENCIES } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Sparkles } from 'lucide-react';

import usePageTitle from '@/hooks/usePageTitle';
const AGE_RANGES = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'];
const COUNTRIES = ['India', 'United States', 'United Arab Emirates', 'United Kingdom', 'European Union', 'Other'];
const EMPLOYMENT = ['Salaried', 'Self-employed', 'Freelancer', 'Business owner', 'Retired'];
const GOALS = [
  'Track expenses', 'Reduce overspending', 'Build emergency fund', 'Save for retirement',
  'Manage loans', 'Build investments', 'Become financially independent',
  'Understand spending patterns', 'Improve savings discipline'
];
const STRESS = [
  "I don't know where my money goes",
  'I overspend frequently',
  'I live paycheck to paycheck',
  'I have debt stress',
  'Medical expenses are affecting finances',
  'I want to invest but feel confused',
  'I want financial clarity'
];

export default function Onboarding() {
  usePageTitle('Welcome');
  const { user, profile, refreshProfile } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    name: '', age_range: '', country: 'India', currency: 'INR', employment_type: '',
    financial_goals: [],
    monthly_income: '', monthly_expenses: '', existing_loans: '', existing_investments: '', passive_income: '', dependents: '',
    stress_points: [],
    want_ai_insights: true, want_weekly_summary: true, want_monthly_reflection: true,
  });

  useEffect(() => {
    if (profile?.onboarding_complete) nav('/dashboard', { replace: true });
    if (profile?.name && !form.name) setForm((f) => ({ ...f, name: profile.name }));
  }, [profile]); // eslint-disable-line

  const toggleArr = (key, val) => setForm((f) => ({
    ...f,
    [key]: f[key].includes(val) ? f[key].filter((v) => v !== val) : [...f[key], val],
  }));

  const steps = [
    { title: 'A little about you', subtitle: 'No judgment, this just helps insights feel relevant.' },
    { title: 'What matters to you', subtitle: 'Choose any goals that resonate.' },
    // Phase 70 — Trust-audit (B1): rephrased subtitle from "Rough numbers are
    // fine. You can edit anytime." (which still implied the numbers were
    // required) to language that explicitly invites skipping. The fields
    // themselves are no longer required for advancing past this step — see
    // the `valid` memo below and the inline privacy note in the form.
    { title: 'Your financial landscape', subtitle: 'Optional, but it helps us calibrate. Add what you know, skip the rest.' },
    { title: 'What feels heavy right now', subtitle: 'No pressure. Pick anything that fits.' },
    { title: 'How you’d like 2120 to show up', subtitle: 'You can change these later in Settings.' },
  ];

  const valid = useMemo(() => {
    if (step === 0) return form.name && form.age_range && form.country && form.currency && form.employment_type;
    // Phase 70 — Trust-audit (B1): step 2 was previously gating "Next" on
    // monthly_income + monthly_expenses being filled in. Beta users were
    // bouncing here because they didn't want to commit sensitive numbers
    // before seeing any value, and the friction made the product feel like
    // a budgeting questionnaire instead of a calm clarity tool. We now let
    // users continue without supplying any numbers — they can add them
    // later in Settings whenever they're ready.
    return true;
  }, [step, form]);

  const finish = async () => {
    if (!user) return;
    setBusy(true);
    try {
      const { error: pErr } = await supabase.from('profiles').update({
        name: form.name,
        age_range: form.age_range,
        country: form.country,
        currency: form.currency,
        employment_type: form.employment_type,
        want_ai_insights: form.want_ai_insights,
        want_weekly_summary: form.want_weekly_summary,
        want_monthly_reflection: form.want_monthly_reflection,
        onboarding_complete: true,
      }).eq('id', user.id);
      if (pErr) throw pErr;

      const { error: oErr } = await supabase.from('onboarding_profiles').upsert({
        user_id: user.id,
        financial_goals: form.financial_goals,
        monthly_income: Number(form.monthly_income || 0),
        monthly_expenses: Number(form.monthly_expenses || 0),
        existing_loans: Number(form.existing_loans || 0),
        existing_investments: Number(form.existing_investments || 0),
        passive_income: Number(form.passive_income || 0),
        dependents: Number(form.dependents || 0),
        stress_points: form.stress_points,
      }, { onConflict: 'user_id' });
      if (oErr) throw oErr;

      await refreshProfile();
      toast.success('All set. Welcome to financial clarity for modern life.');
      // Phase 74 — after essentials, route to the 90-second "get to know you"
      // mini-flow. It's fully skippable; users can complete it later from
      // Settings → Life Profile.
      nav('/welcome', { replace: true });
    } catch (e) {
      toast.error(e.message || 'Could not save onboarding.');
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    if (!valid) {
      toast('A little more detail will make insights feel more accurate.');
      return;
    }
    if (step === steps.length - 1) finish();
    else setStep((s) => s + 1);
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const progress = ((step + 1) / steps.length) * 100;

  return (
    <div className="min-h-screen bg-background bg-hero-soft text-foreground">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <img src="/brand/logo-mark.png" alt="2120" className="h-7 w-7" />
            <img src="/brand/wordmark-green.png" alt="2120" className="h-5 w-auto dark:hidden" />
            <img src="/brand/wordmark-gold.png" alt="2120" className="h-5 w-auto hidden dark:block" />
          </div>
          <Badge variant="secondary" className="rounded-full text-xs" data-testid="onboarding-step-indicator">Step {step + 1} of {steps.length}</Badge>
        </div>
        <Progress value={progress} data-testid="onboarding-progress" className="h-1.5 mb-8" />

        <Card className="rounded-2xl border-border/70">
          <CardContent className="p-6 sm:p-8 space-y-6 fade-in" key={step}>
            <div className="space-y-1.5">
              <div className="inline-flex items-center gap-2 text-xs text-muted-foreground"><Sparkles className="h-3.5 w-3.5" /> Onboarding</div>
              <h2 className="font-display text-2xl font-semibold">{steps[step].title}</h2>
              <p className="text-sm text-muted-foreground">{steps[step].subtitle}</p>
            </div>

            {step === 0 && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>What should we call you?</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" data-testid="onboarding-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Age range</Label>
                  <Select value={form.age_range} onValueChange={(v) => setForm({ ...form, age_range: v })}>
                    <SelectTrigger className="rounded-xl" data-testid="onboarding-age-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{AGE_RANGES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Country</Label>
                  <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                    <SelectTrigger className="rounded-xl" data-testid="onboarding-country-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Currency</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger className="rounded-xl" data-testid="onboarding-currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}, {c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Employment</Label>
                  <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                    <SelectTrigger className="rounded-xl" data-testid="onboarding-employment-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{EMPLOYMENT.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="grid sm:grid-cols-2 gap-2">
                {GOALS.map((g) => {
                  const active = form.financial_goals.includes(g);
                  return (
                    <button
                      key={g}
                      type="button"
                      onClick={() => toggleArr('financial_goals', g)}
                      data-testid={`goal-${g.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                      className={`text-left rounded-xl border px-4 py-3 text-sm transition-colors ${active ? 'border-primary bg-accent text-accent-foreground' : 'border-border/70 hover:border-border bg-card'}`}>
                      <div className="flex items-center justify-between">
                        <span>{g}</span>
                        {active && <Check className="h-4 w-4 text-primary" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <NumField label="Monthly income" value={form.monthly_income} onChange={(v) => setForm({ ...form, monthly_income: v })} testid="onb-income" />
                  <NumField label="Monthly expenses" value={form.monthly_expenses} onChange={(v) => setForm({ ...form, monthly_expenses: v })} testid="onb-expenses" />
                  <NumField label="Existing loans (outstanding)" value={form.existing_loans} onChange={(v) => setForm({ ...form, existing_loans: v })} testid="onb-loans" />
                  <NumField label="Existing investments" value={form.existing_investments} onChange={(v) => setForm({ ...form, existing_investments: v })} testid="onb-investments" />
                  <NumField label="Rental / passive income / month" value={form.passive_income} onChange={(v) => setForm({ ...form, passive_income: v })} testid="onb-passive" />
                  <NumField label="Dependents" value={form.dependents} onChange={(v) => setForm({ ...form, dependents: v })} integer testid="onb-dependents" />
                </div>
                {/* Phase 70 — Trust-audit (B1): inline privacy note next to
                    the sensitive number inputs. The two notes answer the two
                    questions sceptical users have at this exact moment:
                    "do I have to?" and "what happens to my numbers?" */}
                <div className="rounded-xl border border-border/60 bg-muted/30 px-4 py-3 text-xs text-muted-foreground leading-relaxed" data-testid="onb-privacy-note">
                  <p className="text-foreground/80">
                    <span className="font-medium">All of this is optional.</span>{' '}
                    Leave anything blank and we&rsquo;ll learn as you add transactions and statements over time.
                  </p>
                  <p className="mt-1.5">
                    These numbers stay private to your account. We never sell, share, or use your financial data to target ads.
                  </p>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="grid sm:grid-cols-2 gap-2">
                {STRESS.map((s) => {
                  const active = form.stress_points.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleArr('stress_points', s)}
                      data-testid={`stress-${s.toLowerCase().replace(/[^a-z]+/g, '-').slice(0, 30)}`}
                      className={`text-left rounded-xl border px-4 py-3 text-sm transition-colors ${active ? 'border-primary bg-accent text-accent-foreground' : 'border-border/70 hover:border-border bg-card'}`}>
                      <div className="flex items-center justify-between">
                        <span>{s}</span>
                        {active && <Check className="h-4 w-4 text-primary" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {step === 4 && (
              <div className="space-y-4">
                <PrefRow label="AI insights" desc="Calm, plain-language reflections on your money." checked={form.want_ai_insights} onChange={(v) => setForm({ ...form, want_ai_insights: v })} testid="pref-ai" />
                <PrefRow label="Weekly summary" desc="A weekly recap of patterns." checked={form.want_weekly_summary} onChange={(v) => setForm({ ...form, want_weekly_summary: v })} testid="pref-weekly" />
                <PrefRow label="Monthly reflection" desc="A monthly note on what shifted." checked={form.want_monthly_reflection} onChange={(v) => setForm({ ...form, want_monthly_reflection: v })} testid="pref-monthly" />
              </div>
            )}

            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" onClick={back} disabled={step === 0 || busy} data-testid="onboarding-back-button" className="rounded-xl">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button onClick={next} disabled={busy} data-testid="onboarding-next-button" className="rounded-xl">
                {step === steps.length - 1 ? (busy ? 'Saving…' : 'Finish') : 'Continue'}
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, integer, testid }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        type="number"
        inputMode={integer ? 'numeric' : 'decimal'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-xl tabular-nums"
        data-testid={testid}
        placeholder="0"
      />
    </div>
  );
}

function PrefRow({ label, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-border/70 bg-card px-4 py-3">
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{desc}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
